import { ChartCard } from "../components/ChartCard.js";
import type { BarChartItem } from "../components/charts/BarChart.js";
import { BarChart } from "../components/charts/BarChart.js";
import { PieChart } from "../components/charts/PieChart.js";
import { PaymentFunnelByVenueCard } from "../components/dashboard/PaymentFunnelByVenueCard.js";
import { StintsPerUptimeHourCard } from "../components/dashboard/StintsPerUptimeHourCard.js";
import { KpiRow } from "../components/KpiRow.js";
import type { ChartTableSelection } from "../lib/chartTables.js";
import {
  dailyChartAxisLabel,
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
} from "../lib/dashboardDayLabel.js";
import { localHourNow } from "../lib/dashboardTime.js";
import type {
  AcquisitionData,
  CoarsePulseSanityCheck,
  DashboardTabHandler,
  FilterState,
  OpenRawEventsHandler,
} from "../types.js";

function coarsePulseSanityCaption(s: CoarsePulseSanityCheck, fmt: (ms: number) => string): string {
  const slotM = Math.max(1, Math.round(s.nominalSlotMs / 60_000));
  const parts = [
    `Attract ${fmt(s.attractMsRough)} (${s.attractPulses}×${slotM}m)`,
    `Main menu ${fmt(s.menuMsRough)} (${s.mainMenuPulses}×${slotM}m)`,
    `Games ${fmt(s.gamesMsRough)} (${s.gamesPulses}×${slotM}m)`,
  ];
  if (s.otherPulses > 0) {
    parts.push(`${s.otherPulses} pulse(s) other/unmapped screen_name`);
  }
  return `Rough pulse check: ${parts.join(" · ")}`;
}

function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function fmtDurationMs(ms: number): string {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  if (minutes <= 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

const ATTRACT_MS_FILL = "var(--color-warn)";
const MENU_MS_FILL = "var(--color-accent-2)";
const GAMES_MS_FILL = "#5aa2ff";
/** Same purple as Payment Flow Funnel “Interaction Stints”. */
const INTERACTION_STINTS_FILL = "#8b7cf6";
/** Past this, duration labels above bars overlap; hover still has the times. */
const VALUE_LABEL_MAX_BARS = 7;

interface AcquisitionPanelProps {
  data: AcquisitionData | null;
  filters: FilterState;
  reportingTimezone: string;
  onOpenRawEvents: OpenRawEventsHandler;
  onOpenSessions: DashboardTabHandler;
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  chartFullWeeks: boolean;
  onChartFullWeeksChange: (next: boolean) => void;
}

export function AcquisitionPanel({
  data,
  filters,
  reportingTimezone,
  onOpenRawEvents,
  onOpenSessions,
  chartTables,
  onChartTablesChange,
  chartFullWeeks,
  onChartFullWeeksChange,
}: AcquisitionPanelProps) {
  if (!data) {
    return <p className="py-5 text-[13px] text-text-secondary">No data available</p>;
  }

  const { paidSessions, paidSessionsWithGameStart } = data.paidToGameConversion;
  const paidToStartConversion =
    paidSessions > 0 ? fmtPct(paidSessionsWithGameStart / paidSessions) : "—";

  const attractMenuGamesDailyBars: BarChartItem[] = data.attractTimeByDay.map((row) => {
    const totalMs = row.attractMs + row.mainMenuMs + row.gamesMs;
    return {
      ...dailyChartBarAxisProps(row.day),
      value: totalMs,
      valueLabel: fmtDurationMs(totalMs),
      stack: [
        { value: row.attractMs, fill: ATTRACT_MS_FILL },
        { value: row.mainMenuMs, fill: MENU_MS_FILL },
        { value: row.gamesMs, fill: GAMES_MS_FILL },
      ],
    };
  });

  const dailyCompact = attractMenuGamesDailyBars.length > 12;
  const dailyRotate = dailyChartRotateAxisLabels(attractMenuGamesDailyBars.length);
  const showAttractDailyValueLabels =
    attractMenuGamesDailyBars.length > 0 &&
    attractMenuGamesDailyBars.length <= VALUE_LABEL_MAX_BARS;

  const interactionStintsDailyBars: BarChartItem[] = data.interactionStintsByDay.map((row) => ({
    ...dailyChartBarAxisProps(row.day),
    value: row.stintCount,
    fill: INTERACTION_STINTS_FILL,
  }));
  const stintsDailyCompact = interactionStintsDailyBars.length > 12;
  const stintsDailyRotate = dailyChartRotateAxisLabels(interactionStintsDailyBars.length);
  const stintsTotal = data.interactionStintsByDay.reduce((a, r) => a + r.stintCount, 0);

  const interactionStintsHourlyBars: BarChartItem[] = data.interactionStintsByHour.map((row) => ({
    label: String(row.hour),
    value: row.stintCount,
    fill: INTERACTION_STINTS_FILL,
  }));
  const localHourNowIndex = localHourNow(reportingTimezone);

  return (
    <>
      <KpiRow
        items={[
          { name: "Events in scope", value: String(data.eventCount), sub: "metrics-filtered" },
          {
            name: "Attract ratio",
            value: fmtPct(data.attractRatio),
            sub: "share of visible table time",
          },
          {
            name: "Interactive ratio",
            value: fmtPct(data.nonAttractRatio),
            sub: "menu + game exposure",
          },
          {
            name: "Paid to start conversion",
            value: paidToStartConversion,
            sub:
              paidSessions > 0
                ? `paid sessions reaching gameplay (${paidSessionsWithGameStart} / ${paidSessions})`
                : "no paid sessions linked by interaction_session_id in range",
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-[10px] xl:grid-cols-2">
        <StintsPerUptimeHourCard
          rows={data.interactionStintsPerUptimeHourByDay}
          tableCount={data.uniqueTableCount}
          devices={data.chartDevices}
          uptimeByDeviceDay={data.uptimeByDeviceDay}
          stintsByDeviceDay={data.stintsByDeviceDay}
          chartTables={chartTables}
          onChartTablesChange={onChartTablesChange}
          fullWeeks={chartFullWeeks}
          onFullWeeksChange={onChartFullWeeksChange}
          filters={filters}
          onOpenSessions={onOpenSessions}
        />

        <ChartCard
          title="Attract / Main Menu / Games"
          criteria={`Source: device_events (same date/venue/device/game filters as dashboard).

Sums **duration_ms** on **screen_exited** where **properties.screen_name** is **attract**, **main_menu**, or **game** (shell coarse screen dwell on exit).

**Games** is **not** from game_completed / game_abandoned (those durations are cumulative from game_started and can double-count across replays).

**Pulse gap** (extra): when **coarse_uptime_pulse** spacing on a device exceeds ~125% of the prior pulse’s **interval_ms** (default 5m), the excess wall time is added to **attract / main_menu / game**. Bucketing prefers each pulse’s **screen_name** (**menu** counts as main menu), then **screen_viewed** / **screen_exited** history before the gap (same venue/device/date window; **game_url** filter is **not** applied to pulse/nav so gaps still resolve).

Pie area ∝ each sum after merge. **Reconcile with exports:** JSON **attractTime** includes nested **fromScreenExitedMs** and **fromPulseGapMs** (each with attract/menu/games ms). Summing **screen_exited.duration_ms** by **screen_name** matches **fromScreenExitedMs**; pie slice size is **fromScreenExitedMs** plus **fromPulseGapMs** per bucket.

Below the chart: **coarsePulseSanityCheck** — count of **coarse_uptime_pulse** rows per mapped shell screen × **nominalSlotMs** (default 5m); rough wall-clock guide vs pie (different model).

Slice click: Raw Events → **screen_exited** with **screen_name** filter for that slice.`}
          note="Shell dwell + inferred silence after missing coarse pulses (see ⓘ)."
        >
          <PieChart
            slices={[
              { label: "Attract", value: data.attractMs, color: "var(--color-warn)" },
              { label: "Main menu", value: data.mainMenuMs, color: "var(--color-accent-2)" },
              { label: "Games", value: data.gamesMs, color: "#5aa2ff" },
            ]}
            formatValue={fmtDurationMs}
            sliceTooltip={(slice) => {
              const click =
                slice.label === "Attract"
                  ? "Click → Raw Events → screen_exited (screen_name attract)."
                  : slice.label === "Main menu"
                    ? "Click → Raw Events → screen_exited (screen_name main_menu)."
                    : "Click → Raw Events → screen_exited (screen_name game).";
              if (data.gamesMsFromScreenExited == null) {
                return `${slice.label}: ${fmtDurationMs(slice.value)}. ${click}`;
              }
              const dwell =
                slice.label === "Attract"
                  ? data.attractMsFromScreenExited
                  : slice.label === "Main menu"
                    ? data.mainMenuMsFromScreenExited
                    : data.gamesMsFromScreenExited;
              const gap =
                slice.label === "Attract"
                  ? data.attractMsFromPulseGap
                  : slice.label === "Main menu"
                    ? data.mainMenuMsFromPulseGap
                    : data.gamesMsFromPulseGap;
              return `${slice.label}: ${fmtDurationMs(dwell ?? 0)} from screen_exited + ${fmtDurationMs(gap ?? 0)} inferred (pulse gaps) = ${fmtDurationMs(slice.value)}. ${click}`;
            }}
            onSliceClick={(slice, _i, e) => {
              if (slice.label === "Attract") {
                onOpenRawEvents(
                  {
                    eventName: "screen_exited",
                    propertiesContains: `"screen_name":"attract"`,
                  },
                  e,
                );
              } else if (slice.label === "Main menu") {
                onOpenRawEvents(
                  {
                    eventName: "screen_exited",
                    propertiesContains: `"screen_name":"main_menu"`,
                  },
                  e,
                );
              } else {
                onOpenRawEvents(
                  {
                    eventName: "screen_exited",
                    propertiesContains: `"screen_name":"game"`,
                  },
                  e,
                );
              }
            }}
          />
          {data.coarsePulseSanityCheck ? (
            <p className="mt-3 max-w-[520px] text-[12px] leading-snug text-text-secondary">
              {coarsePulseSanityCaption(data.coarsePulseSanityCheck, fmtDurationMs)}. Same
              venue/device/date as coarse_uptime_pulse (no game_url on pulses); each count × nominal
              slot is a wall-clock rough guide only — not additive with the pie.
            </p>
          ) : null}
        </ChartCard>

        <div className="min-w-0 xl:col-span-2">
          <PaymentFunnelByVenueCard
            rows={data.paymentFunnelByVenue}
            filters={filters}
            reportingTimezone={reportingTimezone}
            onOpenRawEvents={onOpenRawEvents}
            onOpenSessions={onOpenSessions}
          />
        </div>

        <ChartCard
          title="Interaction Stints (by day)"
          titleRight={
            <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
              {stintsTotal.toLocaleString()} total
            </span>
          }
          criteria={`Same grain as Payment Flow Funnel **Interaction Stints**: distinct non-empty **interaction_session_id** in the filtered window.

Per day: bucket each stint by the reporting calendar day (**reporting_tz**) of its **first** event in the window (min client_timestamp). Empty days in the filter range are included as 0.

Axis labels: **D/M** + weekday initial (e.g. **31/7 F**). Sat/Sun labels are bold white. When many days, labels rotate −90° so the letter sits against the bars (weekly M·T·W·T·F·S·S pattern).

Bar click: Interaction sessions tab, date range narrowed to that day. Middle-click / Ctrl/Cmd+click: new tab.`}
          note="Daily visitor-stint volume at the table (one id = one stint). Purple matches Payment Flow Funnel Interaction Stints; weekends highlighted on the axis."
        >
          <BarChart
            data={interactionStintsDailyBars}
            fillBars
            compact={stintsDailyCompact}
            verbatimAxisLabels
            rotateAxisLabels={stintsDailyRotate}
            axisLabelMaxChars={10}
            showYAxis
            barTooltip={(item) => {
              const row = data.interactionStintsByDay.find(
                (r) => dailyChartAxisLabel(r.day) === item.label,
              );
              const n = row?.stintCount ?? Math.round(item.value);
              return `${item.label}: ${n.toLocaleString()} interaction stint${n === 1 ? "" : "s"} — click: Sessions tab for this day. Middle-click / Ctrl/Cmd+click: new tab.`;
            }}
            onBarClick={(item, _i, _si, e) => {
              const row = data.interactionStintsByDay.find(
                (r) => dailyChartAxisLabel(r.day) === item.label,
              );
              if (!row?.day) return;
              onOpenSessions(e, { drillDownFrom: row.day, drillDownTo: row.day });
            }}
          />
        </ChartCard>

        <ChartCard
          title="Interaction Stints (by hour of day)"
          titleRight={
            <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
              {stintsTotal.toLocaleString()} total
            </span>
          }
          criteria={`Same grain as **Interaction Stints (by day)**: distinct non-empty **interaction_session_id** in the filtered window.

Per bar: bucket each stint by the reporting-local **hour** (0–23, **reporting_tz**) of its **first** event in the window. Empty hours are included as 0. Totals across hours match the by-day chart when the range has no timezone edge cases spanning midnights differently.

Highlight: current local hour in the reporting timezone. No Raw Events hour filter from this chart.`}
          note="When during the day stints start (local clock). Purple matches Interaction Stints by day."
        >
          <BarChart
            data={interactionStintsHourlyBars}
            fillBars
            compact
            verbatimAxisLabels
            showYAxis
            highlightIndex={localHourNowIndex}
            highlightLabel="Now (local)"
            barTooltip={(item) => {
              const hour = Number(item.label);
              const row = data.interactionStintsByHour.find((r) => r.hour === hour);
              const n = row?.stintCount ?? Math.round(item.value);
              return `Local hour ${item.label}: ${n.toLocaleString()} interaction stint${n === 1 ? "" : "s"} (first event in window). No Raw Events hour filter from this chart.`;
            }}
          />
        </ChartCard>

        <ChartCard
          title="Attract / Main Menu / Games (by day)"
          criteria={`Same definition as the pie (screen_exited dwell + pulse-gap merge), grouped by reporting calendar day (reporting_tz / dashboard timezone).

Each bar is stacked bottom→top: attract (orange), main menu (teal), games (blue). Bar height ∝ total ms that day.

Segment click: Raw Events opens with **screen_exited** + **screen_name** for that layer and switches the dashboard date range to **that day** (custom from/to).

Axis labels: **D/M** + weekday initial (e.g. **31/7 F**); Sat/Sun bold white. Dense ranges rotate −90°. Duration above bars and counts inside stacks are omitted when there are more than 7 days (hover still shows slice duration).`}
          note="Per-day share of exposure time; hover a segment for slice duration."
        >
          <BarChart
            data={attractMenuGamesDailyBars}
            fillBars
            compact={dailyCompact}
            verbatimAxisLabels
            rotateAxisLabels={dailyRotate}
            axisLabelMaxChars={10}
            showValueLabels={showAttractDailyValueLabels}
            hideStackSegmentLabels={!showAttractDailyValueLabels}
            barTooltip={(item, _dayIndex, segmentIndex) => {
              const row = data.attractTimeByDay.find(
                (r) => dailyChartAxisLabel(r.day) === item.label,
              );
              if (!row) return item.label;
              if (segmentIndex === undefined) {
                return `${row.day}: ${fmtDurationMs(row.attractMs + row.mainMenuMs + row.gamesMs)} total`;
              }
              const layers: [string, number][] = [
                ["Attract", row.attractMs],
                ["Main menu", row.mainMenuMs],
                ["Games", row.gamesMs],
              ];
              const [label, ms] = layers[segmentIndex] ?? ["", 0];
              return `${row.day} · ${label}: ${fmtDurationMs(ms)} — click: Raw Events + narrow to this day.`;
            }}
            onBarClick={(item, _dayIndex, segmentIndex, e) => {
              if (segmentIndex === undefined) return;
              const row = data.attractTimeByDay.find(
                (r) => dailyChartAxisLabel(r.day) === item.label,
              );
              if (!row?.day) return;
              const drill = { drillDownFrom: row.day, drillDownTo: row.day };
              if (segmentIndex === 0) {
                onOpenRawEvents(
                  {
                    ...drill,
                    eventName: "screen_exited",
                    propertiesContains: `"screen_name":"attract"`,
                  },
                  e,
                );
              } else if (segmentIndex === 1) {
                onOpenRawEvents(
                  {
                    ...drill,
                    eventName: "screen_exited",
                    propertiesContains: `"screen_name":"main_menu"`,
                  },
                  e,
                );
              } else {
                onOpenRawEvents(
                  {
                    ...drill,
                    eventName: "screen_exited",
                    propertiesContains: `"screen_name":"game"`,
                  },
                  e,
                );
              }
            }}
          />
        </ChartCard>

        <ChartCard
          title="Main Menu Outcome Split"
          criteria={`Same interaction_session_id grain as menu dwell: menu exits with duration, split by whether game_started occurred in that session.

Counts: sessions with menu + game_started vs menu without game_started.

Slice click: To game → game_started; To attract → screen_exited main_menu (approximate “stayed in menu” lens).`}
          note="How many menu sessions go to game versus back to attract."
        >
          <PieChart
            slices={[
              { label: "To game", value: data.menuOutcome.toGame, color: "var(--color-accent-2)" },
              {
                label: "To attract",
                value: data.menuOutcome.toAttract,
                color: "var(--color-warn)",
              },
            ]}
            sliceTooltip={(slice) =>
              slice.label === "To game"
                ? "Click: Raw Events → game_started."
                : "Click: Raw Events → screen_exited (main_menu) — chart bucket is ‘no game start in session’."
            }
            onSliceClick={(slice, _i, e) => {
              if (slice.label === "To game") {
                onOpenRawEvents({ eventName: "game_started" }, e);
              } else {
                onOpenRawEvents(
                  {
                    eventName: "screen_exited",
                    propertiesContains: `"screen_name":"main_menu"`,
                  },
                  e,
                );
              }
            }}
          />
        </ChartCard>

        <ChartCard
          title="Interaction session duration"
          titleRight={
            <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
              {data.interactionSessionDurationHist.reduce((a, b) => a + b, 0).toLocaleString()}{" "}
              sessions
            </span>
          }
          criteria={`Semantics (games shell, see docs/session-model-and-repos.md): interaction_session_id = one visitor stint from first touch on attract through menu/game until the shell issues a new id — after menu/game→attract, or 5 min idle on attract only (menu cancels attract idle). Early events may omit the id until that first attract touch.

What this chart measures: distinct non-empty interaction_session_id in the same filters as this tab. Per id: span_ms = max(client_timestamp) − min(client_timestamp) across that id’s rows in the window (wall clock, not sum of duration_ms). Buckets: <1m, 1–5m, 5–15m, 15–30m, 30m–1h, 1h+ (ms: 60k, 300k, 900k, 1.8M, 3.6M).

Span can exceed “true” stint if the window cuts off start/end, or include long gaps between any logged events. Raw Events has no session-span filter; bar click is not wired.`}
          note="Clock span of stored events per id in this range (gaps included). One id = one stint from first attract interaction; see ⓘ for shell rotation rules."
        >
          <BarChart
            data={[
              { label: "<1m", value: data.interactionSessionDurationHist[0] ?? 0 },
              { label: "1-5m", value: data.interactionSessionDurationHist[1] ?? 0 },
              { label: "5-15m", value: data.interactionSessionDurationHist[2] ?? 0 },
              { label: "15-30m", value: data.interactionSessionDurationHist[3] ?? 0 },
              { label: "30m-1h", value: data.interactionSessionDurationHist[4] ?? 0 },
              { label: "1h+", value: data.interactionSessionDurationHist[5] ?? 0 },
            ]}
            barTooltip={(item) =>
              `${item.label}: ${Math.round(item.value)} sessions — last−first event time for this id in range (gaps count). ID = one stint from first attract touch; new id after menu/game→attract or 5 min idle on attract only.`
            }
          />
        </ChartCard>

        <ChartCard
          title="Main Menu Dwell (Game Starts Only)"
          criteria={`Per interaction_session_id with non-null interaction_session_id:

• menu_dwell_ms = sum(duration_ms) for screen_exited where screen_name = main_menu
• only sessions with menu_exit_count > 0 and game_start_count > 0 (menu session also had game_started)

Histogram: count sessions by menu_dwell_ms bucket (<15s, 15–30s, …).

Bar click: Raw Events → screen_exited with Properties contains main_menu (dwell bucket is not applied in Raw).`}
          note="Only menu sessions that start gameplay. TODO: Add paid vs free filter."
        >
          <BarChart
            data={[
              { label: "<15s", value: data.menuBinsPct[0] ?? 0 },
              { label: "15-30s", value: data.menuBinsPct[1] ?? 0 },
              { label: "30-60s", value: data.menuBinsPct[2] ?? 0 },
              { label: "1-2m", value: data.menuBinsPct[3] ?? 0 },
              { label: "2m+", value: data.menuBinsPct[4] ?? 0 },
            ]}
            percent
            barTooltip={(item) =>
              `${item.label}: ${Math.round(item.value * 100)}% of qualifying menu→game sessions — click: Raw Events → screen_exited (main_menu). Middle-click / Ctrl/Cmd+click: new tab.`
            }
            onBarClick={(_item, _i, _si, e) =>
              onOpenRawEvents(
                {
                  eventName: "screen_exited",
                  propertiesContains: `"screen_name":"main_menu"`,
                },
                e,
              )
            }
          />
        </ChartCard>

        <ChartCard title="Acquisition Notes" criteria="Text-only card; no chart query." note="">
          <div className="mb-2 flex flex-wrap gap-[10px] text-xs text-text-secondary">
            <span>Menu views: {data.funnel.menu}</span>
            <span>Tutorial opens: {data.funnel.tutorial}</span>
            <span>Payment opens: {data.funnel.paymentOpen}</span>
          </div>
          <p className="py-5 text-[13px] text-text-secondary">
            Apply game, location, and happy-hour filters to compare conversion patterns by venue
            context.
          </p>
        </ChartCard>
      </div>
    </>
  );
}
